import type { GridColDef } from '@mui/x-data-grid';
import { useSession } from '@side/next-auth/react';
import { toast } from '@side/pantry';
import { addMonths, format, parse, subDays, subYears } from 'date-fns';
import { chunk, clamp } from 'lodash';
import { useRouter } from 'next/router';
import { useEffectOnce } from 'react-use';
import { formatPrice } from '@/common/utils/money';
import { EmptyTableCell } from '@/components/EmptyTableCell';
import { identityApi, paymentApi } from '$api-clients';
import type { PaymentSchema } from '$api-clients/generated';
import type { PaymentRequest } from '$api-clients/hooks';
import type { CompensationReportFiltersFormValues } from './CompensationReportFilters/CompensationReportFilters.helpers';
import {
  type PerformanceReportFiltersFormValues,
  PerformanceReportFilterTransactionStatus,
  PerformanceReportFilterTransactionType,
} from './PerformanceReport/types';
import {
  type ReportsDataGroup,
  useRoleCanSee,
} from './ReportsConfiguration/useReportsRole';
import {
  IDS_CHUNK_SIZE,
  REPORTING_PERIOD_ALL_TIME,
  REPORTING_PERIOD_CUSTOM,
  REPORTING_PERIOD_L365,
  type ReportingPeriod,
  type ReportingSummaryQuery,
  type TeamDisplayMap,
  type UserDisplayMap,
} from './types';

export function isReportingPeriodL365(
  period: ReportingPeriod | undefined,
): boolean {
  return period === REPORTING_PERIOD_L365;
}

export function isReportingPeriodCustom(
  period: ReportingPeriod | undefined,
): boolean {
  return period === REPORTING_PERIOD_CUSTOM;
}

export function isReportingPeriodAllTime(
  period: ReportingPeriod | undefined,
): boolean {
  return period === REPORTING_PERIOD_ALL_TIME;
}

export function shouldUseYearlyReportingPeriod(
  period: ReportingPeriod | undefined,
): boolean {
  return (
    !isReportingPeriodL365(period) &&
    !isReportingPeriodCustom(period) &&
    !isReportingPeriodAllTime(period)
  );
}

/**
 * Goal data and prior-year comparisons are calendar-year concepts; they
 * don't apply to rolling/custom/all-time windows.
 */
export function shouldHidePeriodComparisons(
  period: ReportingPeriod | undefined,
): boolean {
  return !shouldUseYearlyReportingPeriod(period);
}

export function getAllTimePeriods(
  allTimeStartDate: string | undefined,
): { year: number; month: number }[] | null {
  if (!allTimeStartDate) return null;
  return getPeriodsForDateRange(
    allTimeStartDate,
    format(new Date(), 'yyyy-MM-dd'),
  );
}

/**
 * Returns an array of { year, month } for every calendar month between
 * startDate and endDate (inclusive), ordered chronologically.
 */
export function getPeriodsForDateRange(
  startDate: string,
  endDate: string,
): { year: number; month: number }[] {
  const [startYear, startMonth] = startDate.split('-').map(Number);
  const [endYear, endMonth] = endDate.split('-').map(Number);

  const periods: { year: number; month: number }[] = [];
  let current = new Date(startYear, startMonth - 1, 1);
  const end = new Date(endYear, endMonth - 1, 1);

  while (current <= end) {
    periods.push({
      year: current.getFullYear(),
      month: current.getMonth() + 1,
    });
    current = addMonths(current, 1);
  }

  return periods;
}

export function getLast13Periods(): { year: number; month: number }[] {
  const periods: { year: number; month: number }[] = [];
  const d = new Date();
  let i = 12;
  while (i >= 0) {
    const date = new Date(d.getFullYear(), d.getMonth() - i, 1);
    periods.push({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
    });
    i -= 1;
  }
  return periods;
}

// 364 because we include the current day in the calculation
const YEAR_IN_DAYS = 364;

/** Returns closingDateStart and closingDateEnd for the given reporting period. */
export function getReportingPeriodDateRange(
  period: ReportingPeriod | undefined,
  customDates?: { start?: string; end?: string },
  allTimeStartDate?: string,
): {
  closingDateStart: string | undefined;
  closingDateEnd: string | undefined;
} {
  if (period == null) {
    return { closingDateStart: undefined, closingDateEnd: undefined };
  }
  if (isReportingPeriodL365(period)) {
    const end = new Date();
    const start = subDays(end, YEAR_IN_DAYS);
    return {
      closingDateStart: format(start, 'yyyy-MM-dd'),
      closingDateEnd: format(end, 'yyyy-MM-dd'),
    };
  }
  if (isReportingPeriodCustom(period)) {
    return {
      closingDateStart: customDates?.start,
      closingDateEnd: customDates?.end,
    };
  }
  if (isReportingPeriodAllTime(period)) {
    return {
      closingDateStart: allTimeStartDate,
      closingDateEnd: allTimeStartDate
        ? format(new Date(), 'yyyy-MM-dd')
        : undefined,
    };
  }
  const year = typeof period === 'number' ? period : new Date().getFullYear();
  return {
    closingDateStart: `${year}-01-01`,
    closingDateEnd: `${year}-12-31`,
  };
}

/** Returns closingDateStart and closingDateEnd for the prior reporting period. */
export function getPriorReportingPeriodDateRange(
  period: ReportingPeriod | undefined,
  customDates?: { start?: string; end?: string },
): {
  closingDateStart: string | undefined;
  closingDateEnd: string | undefined;
} {
  if (isReportingPeriodL365(period) || isReportingPeriodCustom(period)) {
    const { closingDateStart, closingDateEnd } = getReportingPeriodDateRange(
      period,
      customDates,
    );
    return {
      closingDateStart: shiftBackOneYear(closingDateStart),
      closingDateEnd: shiftBackOneYear(closingDateEnd),
    };
  }
  return getReportingPeriodDateRange(
    typeof period === 'number' ? period - 1 : undefined,
  );
}

const shiftBackOneYear = (date: string | undefined) =>
  date
    ? format(subYears(parse(date, 'yyyy-MM-dd', new Date()), 1), 'yyyy-MM-dd')
    : undefined;

export function buildLeadSourceFilter(
  filters: { leadSources?: { label: string; value: string }[] } | undefined,
) {
  return filters?.leadSources && filters.leadSources.length > 0
    ? {
        leadSourceName: filters.leadSources.map((source) => source.label),
      }
    : {};
}

const DEFAULT_TRANSACTION_TYPES = [
  PerformanceReportFilterTransactionType.listing,
  PerformanceReportFilterTransactionType.offer,
];

export function buildReportingSummaryQuery(
  filters: CompensationReportFiltersFormValues | undefined,
  viewReportAs: string,
  allTimeStartDate?: string,
): Partial<ReportingSummaryQuery> {
  const { closingDateStart, closingDateEnd } = getReportingPeriodDateRange(
    filters?.reportingPeriod,
    { start: filters?.customDateStart, end: filters?.customDateEnd },
    allTimeStartDate,
  );
  const leadSourceFilter = buildLeadSourceFilter(filters);
  return {
    agentIdentityIds: filters?.agents?.map((agent) => agent.value),
    transactionType: filters?.transactionType,
    excludeTerminatedAgents: filters?.hideTerminatedAgents ?? false,
    closingDateStart,
    closingDateEnd,
    teamIdentityIds: filters?.teams?.map((team) => team.value),
    ...leadSourceFilter,
    transactionStatus: ['CLOSED'],
    viewReportAs,
    offset: 0,
    limit: 100,
  };
}

export function buildReportingSummaryPerformanceQuery(
  filters: PerformanceReportFiltersFormValues | undefined,
  viewReportAs: string,
  allTimeStartDate?: string,
): Partial<ReportingSummaryQuery> {
  const { closingDateStart, closingDateEnd } = getReportingPeriodDateRange(
    filters?.reportingPeriod,
    { start: filters?.customDateStart, end: filters?.customDateEnd },
    allTimeStartDate,
  );
  const leadSourceFilter = buildLeadSourceFilter(filters);

  return {
    agentIdentityIds: filters?.agents?.map((agent) => agent.value) ?? [],
    excludeTerminatedAgents: filters?.hideTerminatedAgents ?? false,
    closingDateStart,
    closingDateEnd,
    teamIdentityIds: filters?.teams?.map((team) => team.value),
    ...leadSourceFilter,
    transactionStatus: filters?.status.map((status) => {
      if (status === PerformanceReportFilterTransactionStatus.closed) {
        return 'CLOSED';
      }
      return 'PENDING_OR_IN_ESCROW';
    }),
    transactionType: filters?.transactionType.length
      ? filters?.transactionType
      : DEFAULT_TRANSACTION_TYPES,
    viewReportAs,
  };
}

export function useViewReportAs() {
  const { data: session } = useSession();
  const { query } = useRouter();

  return query.viewReportAs?.toString() ?? session?.user?.id;
}

/**
 * On mount, clear selected agents/teams when the view-as user has changed
 * since the parent last rendered — their IDs scope to the previous user and
 * are invalid against the new user's options, which surfaces as DD warnings.
 */
export function useClearAgentsAndTeamsOnViewAsSwitch<
  T extends { agents: unknown[]; teams: unknown[] },
>(
  previousViewReportAs: string | undefined,
  currentViewReportAs: string | undefined,
  setFilters: (updater: (prev: T) => T) => void,
) {
  useEffectOnce(() => {
    if (
      previousViewReportAs !== undefined &&
      previousViewReportAs !== currentViewReportAs
    ) {
      setFilters((prev) => ({ ...prev, agents: [], teams: [] }));
    }
  });
}

/**
 * Fetches users and teams by IDs present in the given results and returns
 * display maps for agent and team names.
 */
export async function fetchUsersAndTeamsMaps({
  userIds,
  teamIds,
}: {
  userIds: (string | undefined)[];
  teamIds: (string | undefined)[];
}): Promise<{ usersById: UserDisplayMap; teamsById: TeamDisplayMap }> {
  const uniqueUserIds = [
    ...new Set(userIds.filter((id): id is string => !!id)),
  ];
  const uniqueTeamIds = [
    ...new Set(teamIds.filter((id): id is string => !!id)),
  ];

  const usersById: UserDisplayMap = {};
  const teamsById: TeamDisplayMap = {};

  if (uniqueUserIds.length === 0 && uniqueTeamIds.length === 0) {
    return { usersById, teamsById };
  }

  const userChunks = chunk(uniqueUserIds, IDS_CHUNK_SIZE);
  const teamChunks = chunk(uniqueTeamIds, IDS_CHUNK_SIZE);

  const [usersResponses, teamsResponses] = await Promise.all([
    Promise.all(
      userChunks.map((chunk) =>
        identityApi.GET('/users', {
          params: { query: { ids: chunk.join(',') } },
        }),
      ),
    ),
    Promise.all(
      teamChunks.map((chunk) =>
        identityApi.GET('/teams', {
          params: { query: { ids: chunk.join(',') } },
        }),
      ),
    ),
  ]);

  usersResponses.forEach((response) => {
    if (response.data?.data) {
      response.data.data.forEach((user) => {
        usersById[user.id] = user;
      });
    }
  });

  teamsResponses.forEach((response) => {
    if (response.data?.data) {
      response.data.data.forEach((team) => {
        teamsById[team.id] = team;
      });
    }
  });

  return { usersById, teamsById };
}

export function useFilterColumnsByUserRole<T extends object, V extends string>({
  columns,
  dataGroups,
}: {
  columns: GridColDef<T>[];
  dataGroups: Record<V, ReportsDataGroup>;
}) {
  const { roleCanSee } = useRoleCanSee();

  return columns.filter((column) => roleCanSee(dataGroups[column.field as V]));
}

export const EXPORT_PAGE_SIZE = 10000;

export const fetchAllPagesReportingSummaryPages = async ({
  accumulatedResults = [],
  offset,
  query,
}: {
  accumulatedResults?: PaymentRequest<'/reporting/summary'>['Data']['results'];
  offset: number;
  query: Partial<
    PaymentSchema.paths['/reporting/summary']['get']['parameters']['query']
  >;
}): Promise<PaymentRequest<'/reporting/summary'>['Data']['results'] | null> => {
  const response = await paymentApi.GET('/reporting/summary', {
    params: {
      query: {
        ...query,
        offset,
        limit: EXPORT_PAGE_SIZE,
      },
    },
  });

  if (
    response.error !== undefined ||
    !response.data.results ||
    !response.data.totalCount
  ) {
    toast(
      'error',
      'Failed to fetch data. Please try again or contact Side Support for assistance.',
      { showCloseButton: true },
    );
    return null;
  }

  const { results: pageResults, totalCount } = response.data;
  const results = [...accumulatedResults, ...pageResults];

  if (results.length >= totalCount) {
    return results;
  }

  return fetchAllPagesReportingSummaryPages({
    accumulatedResults: results,
    offset: offset + pageResults.length,
    query,
  });
};

const abbreviatedDecimals = (divided: number) => {
  if (divided >= 100) return 1;
  if (divided >= 10) return 2;
  return 3;
};

export type FormatAbbreviatedPriceOptions = {
  /** Omit fractional trailing zeros (e.g. `$1.000M` → `$1M`) while keeping max precision caps. */
  trimTrailingZeros?: boolean;
};

export const formatAbbreviatedPrice = (
  value: number | null,
  { trimTrailingZeros = false }: FormatAbbreviatedPriceOptions = {},
) => {
  if (value === null) {
    return '-';
  }

  const fractionDigits = (decimals: number) => ({
    minimumFractionDigits: trimTrailingZeros ? 0 : decimals,
    maximumFractionDigits: decimals,
  });

  if (value >= 1_000_000_000) {
    const divided = value / 1_000_000_000;
    const decimals = abbreviatedDecimals(divided);
    return `$${divided.toLocaleString(undefined, fractionDigits(decimals))}B`;
  }
  if (value >= 1_000_000) {
    const divided = value / 1_000_000;
    const decimals = abbreviatedDecimals(divided);
    return `$${divided.toLocaleString(undefined, fractionDigits(decimals))}M`;
  }
  if (value >= 100_000) {
    const divided = value / 1_000;
    if (Math.round(divided * 10) >= 10_000) {
      const dividedM = value / 1_000_000;
      const decimals = abbreviatedDecimals(dividedM);
      return `$${dividedM.toLocaleString(undefined, fractionDigits(decimals))}M`;
    }
    return `$${divided.toLocaleString(undefined, fractionDigits(1))}K`;
  }
  return `$${Math.round(value).toLocaleString()}`;
};

export function formatCompactPrice(value: number): string {
  const formatCompactUnit = (scaledValue: number, suffix: 'K' | 'M') => {
    // Normalize floating-point noise (e.g. 80.0000001) while preserving quarter steps.
    const normalized = Number(scaledValue.toFixed(2));
    return `$${Number.isInteger(normalized) ? normalized : normalized}${suffix}`;
  };

  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return formatCompactUnit(value / 1_000_000, 'M');
  }
  if (abs >= 1_000) {
    return formatCompactUnit(value / 1_000, 'K');
  }
  return formatPrice(value, true, false);
}

export const formatAmount = (value: number | null) => {
  if (value == null) return '';

  const price = formatPrice(value.toString(), true, false);

  return value < 0 ? `-${price}` : price;
};

export const formatDecimal = (value: number | null, decimals = 2): string => {
  if (value == null) return '';
  const factor = 10 ** decimals;
  return String(Math.trunc(value * factor) / factor);
};

export const formatPercentage = (value: number | null) => {
  if (value == null) return '';
  return `${value.toString()}%`;
};

export const getPercentage = (current: number | null, total: number | null) => {
  if (!total || current == null) {
    return 0;
  }
  const percentage = (current / total) * 100;
  return Math.round(clamp(percentage, 0, 100));
};

export const createLoadingRows = <T extends object>(
  rowCount: number,
  makeRow: (index: number) => T,
): T[] => Array.from({ length: rowCount }, (_, i) => makeRow(i));

export const mapDataGridColumnToValueOrEmpty = <R extends object>(
  column: GridColDef<R>,
  excludedFields?: string[],
): GridColDef<R> => {
  if (excludedFields?.includes(column.field)) {
    return column;
  }
  return {
    ...column,
    renderCell: (params) => {
      if (params.value == null) return <EmptyTableCell />;
      if (column.renderCell) return column.renderCell(params);
      return params.formattedValue ?? params.value;
    },
  };
};
